import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { CategoryOptionDto, OptionType, RoomCombination } from "../../types";
import { getCategoryOptionsByType } from "../../services/api";
import ProfitRoomModal from "./ProfitRoomModal";

interface Hotel {
  id: string;
  name: string;
  subdomain: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  profitroomCode?: string;
}

interface CategoryDropdownProps {
  categoryType: OptionType;
  isOpen: boolean;
  onClose: () => void;
  onAddAll: (options: CategoryOptionDto[]) => void;
  onAddSingle: (option: CategoryOptionDto) => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  hotelContext?: Hotel;
  offerDates?: {
    checkIn: string;
    checkOut: string;
  };
  onAddProfitRooms?: (combinations: RoomCombination[]) => void;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  categoryType,
  isOpen,
  onClose,
  onAddAll,
  onAddSingle,
  buttonRef,
  hotelContext,
  offerDates,
  onAddProfitRooms,
}) => {
  const [options, setOptions] = useState<CategoryOptionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showProfitRoomModal, setShowProfitRoomModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const categoryLabels = {
    accommodation: "Noclegi",
    gastronomy: "Gastronomia",
    conference: "Konferencje",
    additional: "Dodatki",
  };

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCategoryOptionsByType(categoryType);
      setOptions(data);
    } catch (error) {
      console.error("Failed to load category options:", error);
    } finally {
      setLoading(false);
    }
  }, [categoryType]);

  const updatePosition = useCallback(() => {
    if (buttonRef.current && isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 320; // w-80 = 320px
      const dropdownHeight = 300; // estimated max height

      // For fixed positioning, use viewport coordinates (no scrollY/scrollX)
      let top = rect.bottom + 2;
      let left = rect.left;

      // Check if dropdown would go outside viewport horizontally
      if (left + dropdownWidth > window.innerWidth) {
        left = rect.right - dropdownWidth;
      }

      // Check if dropdown would go outside viewport vertically
      if (rect.bottom + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 2;
      }

      // Ensure dropdown doesn't go off-screen
      left = Math.max(
        10,
        Math.min(left, window.innerWidth - dropdownWidth - 10)
      );
      top = Math.max(10, top);

      setPosition({ top, left });
    }
  }, [buttonRef, isOpen]);

  const filteredOptions = options.filter((option) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return option.name?.toLowerCase().includes(query);
  });

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escaped})`, "gi"));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      loadOptions();
      updatePosition();
    }
  }, [isOpen, categoryType, loadOptions, updatePosition]);

  useEffect(() => {
    if (isOpen && !loading && options.length > 0) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, loading, options.length]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close dropdown when Profitroom modal is open
      if (showProfitRoomModal) {
        return;
      }

      // Check if click is within Profitroom modal
      const target = event.target as Element;
      const isProfitRoomModal = target.closest(
        '[data-profitroom-modal="true"]'
      );

      if (isProfitRoomModal) {
        return; // Don't close dropdown if clicking in modal
      }

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef, showProfitRoomModal]);

  const handleAddAll = () => {
    onAddAll(filteredOptions);
    onClose();
  };

  const handleAddSingle = (option: CategoryOptionDto) => {
    onAddSingle(option);
    // Nie zamykamy popup-a, żeby można było dodać więcej pozycji
  };

  const handleAddFromProfitroom = () => {
    setShowProfitRoomModal(true);
  };

  const handleProfitRoomModalClose = () => {
    setShowProfitRoomModal(false);
  };

  const handleSelectProfitRooms = (combinations: RoomCombination[]) => {
    if (onAddProfitRooms) {
      onAddProfitRooms(combinations);
    }
    setShowProfitRoomModal(false);
    onClose(); // Close the dropdown after adding Profitroom rooms
  };

  // Check if hotel has Profitroom integration
  const hasProfitRoomIntegration =
    categoryType === "accommodation" &&
    hotelContext?.profitroomCode &&
    offerDates?.checkIn &&
    offerDates?.checkOut;

  if (!isOpen) return null;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="fixed z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">
            {categoryLabels[categoryType]}
          </h4>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4 text-gray-500">Ładowanie...</div>
        ) : options.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            Brak opcji w tej kategorii
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="relative mb-3">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    onClose();
                  }
                }}
                placeholder="Szukaj..."
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            <button
              onClick={handleAddAll}
              className="w-full mb-3 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Dodaj wszystkie ({filteredOptions.length})
            </button>

            {/* Profitroom integration button */}
            {hasProfitRoomIntegration && (
              <button
                onClick={handleAddFromProfitroom}
                className="w-full mb-3 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
              >
                🏨 Dodaj z Profitroom
              </button>
            )}

            <div className="space-y-1 max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  Nie znaleziono wyników
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {highlightText(option.name, searchQuery)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {option.defaultUnitPrice} zł
                        {option.personsPerRoom &&
                          ` (${option.personsPerRoom} os.)`}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddSingle(option)}
                      className="ml-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                    >
                      Dodaj
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {createPortal(dropdownContent, document.body)}

      {/* Profitroom Modal */}
      {hasProfitRoomIntegration &&
        hotelContext?.profitroomCode &&
        offerDates && (
          <ProfitRoomModal
            isOpen={showProfitRoomModal}
            onClose={handleProfitRoomModalClose}
            offerDates={offerDates}
            availableOptions={options}
            onSelectRooms={handleSelectProfitRooms}
          />
        )}
    </>
  );
};

export default CategoryDropdown;
